import { createContext, type ReactNode, useContext } from 'react'
import { Platform, View, type StyleProp, type ViewStyle } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { COLORS } from '@/lib/brand'
import { useReducedTransparency } from '@/lib/hooks/use-reduced-transparency'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, withAlpha } from '@/lib/theme/tokens'
import { resolveLiquidCanvasLayer } from '@/lib/theme/liquid-canvas'

const LiquidCanvasContext = createContext(false)

// React Native Web moved `pointerEvents` to the style object. Native still
// needs the prop for hit testing, so keep the platform branch at the shared
// ambient overlay rather than making every screen choose one API or the other.
const nonInteractiveOverlayProps =
  Platform.OS === 'web' ? {} : ({ pointerEvents: 'none' as const } as const)
const nonInteractiveOverlayWebStyle: ViewStyle | undefined =
  Platform.OS === 'web' ? ({ pointerEvents: 'none' } as ViewStyle) : undefined

/**
 * React Navigation preserves React context across a native Screen boundary,
 * but that boundary is not a child in Android's actual drawing hierarchy.
 *
 * Consequently, a route can correctly *see* AppShell's canvas context while
 * its native scene still sits over the platform's default white window. Route
 * roots must therefore start one material plane of their own; ordinary
 * descendants continue to inherit it and never stack gradients.
 */
export function LiquidCanvasSceneBoundary({ children }: { children: ReactNode }) {
  return <LiquidCanvasContext.Provider value={false}>{children}</LiquidCanvasContext.Provider>
}

type LiquidCanvasProps = {
  children: ReactNode
  style?: StyleProp<ViewStyle>
  /** Set false only when a full-bleed map or video owns every pixel. */
  ambient?: boolean
  /**
   * Create a fresh visual plane inside a native modal or an independent flow.
   * Ordinary route content must inherit AppShell's canvas instead: nesting
   * another full-screen gradient is visually redundant and costs frames.
   */
  isolate?: boolean
  testID?: string
}

/**
 * The quiet visual field behind every 2.0 screen.
 *
 * It is deliberately built from three static GPU-friendly gradients rather
 * than a runtime blur. That makes the system feel dimensional on Android
 * while keeping gestures and text-entry responsive on the Redmi. Glass is a
 * material above this field; the field itself never competes with content.
 */
export function LiquidCanvas({
  children,
  style,
  ambient = true,
  isolate = false,
  testID,
}: LiquidCanvasProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedTransparency = useReducedTransparency()
  const hasAncestorCanvas = useContext(LiquidCanvasContext)
  const layer = resolveLiquidCanvasLayer({
    hasAncestorCanvas,
    isolate,
    ambient,
    reducedTransparency,
  })

  return (
    <LiquidCanvasContext.Provider value>
      <View
        testID={testID}
        style={[
          {
            flex: 1,
            overflow: 'hidden',
            // A nested scene stays transparent so the shell canvas remains
            // visible. An isolated modal/root retains the semantic base even
            // when reduced transparency disables its ornamental gradients.
            backgroundColor: layer.ownsCanvas ? colors.background : 'transparent',
          },
          style,
        ]}
      >
        {layer.rendersAmbient ? (
          <View
            {...nonInteractiveOverlayProps}
            style={[{ position: 'absolute', inset: 0 }, nonInteractiveOverlayWebStyle]}
          >
            <LinearGradient
              colors={
                isDark ? ['#100D1B', '#18142B', '#110E1C'] : ['#FCFBFF', '#F3F1FA', '#FAF8FC']
              }
              start={{ x: 0.08, y: 0 }}
              end={{ x: 0.92, y: 1 }}
              style={{ position: 'absolute', inset: 0 }}
            />
            <LinearGradient
              colors={
                isDark
                  ? [withAlpha(COLORS.purple.light, 0.28), withAlpha(COLORS.purple.light, 0)]
                  : [withAlpha(COLORS.purple.light, 0.13), withAlpha(COLORS.purple.light, 0)]
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                position: 'absolute',
                width: 360,
                height: 360,
                borderRadius: 180,
                top: -164,
                left: -118,
                transform: [{ rotate: '-18deg' }],
              }}
            />
            <LinearGradient
              colors={
                isDark
                  ? [withAlpha(COLORS.coral.DEFAULT, 0.2), withAlpha(COLORS.coral.DEFAULT, 0)]
                  : [withAlpha(COLORS.coral.light, 0.11), withAlpha(COLORS.coral.light, 0)]
              }
              start={{ x: 0.14, y: 0 }}
              end={{ x: 0.9, y: 1 }}
              style={{
                position: 'absolute',
                width: 330,
                height: 330,
                borderRadius: 165,
                right: -148,
                bottom: -142,
                transform: [{ rotate: '28deg' }],
              }}
            />
          </View>
        ) : null}
        {children}
      </View>
    </LiquidCanvasContext.Provider>
  )
}
